import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { parseJwt, setSession } from './utils';
import {
  ActionMapType,
  AuthStateType,
  AuthUserType,
  GoogleLogin,
  JWTContextType,
  Register,
} from './types';

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

const API_URL = import.meta.env.VITE_API_URL;

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('token') : '';

      if (accessToken) {
        setSession(accessToken);
        const user = parseJwt(accessToken);

        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const response = await axios.post('/auth/login', {
      email,
      password,
    });
    const { token } = response.data;

    if (!token) throw new Error('Wrong Email or Password');

    const user = parseJwt(token);

    setSession(token);

    dispatch({
      type: Types.LOGIN,
      payload: {
        // @ts-ignore
        user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (props: Register) => {
    const { userType, ...rest } = props;

    const response = await axios.post(`${API_URL}/auth/${userType}/registration`, {
      ...rest,
    });

    if (!response.data.status) {
      throw Object.assign(new Error(response.data.result), { code: 403 });
    }
  }, []);

  // GOOGLE AUTH
  const googleLogin = useCallback(async (props: GoogleLogin) => {
    const { userType, googleToken } = props;

    const requestOptions = {
      method: 'POST', // Specify the method
      headers: {
        'Content-Type': 'application/json', // Inform the server the body format
      },
      body: JSON.stringify({
        idToken: googleToken,
        role: userType,
      }),
    };

    const request = await fetch(`${API_URL}/auth/google`, requestOptions);
    const response = await request.json();

    const { token } = response;

    if (!token) {
      return response;
    }

    const user = parseJwt(token);

    setSession(token);

    dispatch({
      type: Types.LOGIN,
      payload: {
        // @ts-ignore
        user,
      },
    });

    return response;
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    await axios.post('/auth/logout');
    localStorage.removeItem('token');
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      googleLogin,
      loginWithGoogle: () => {},
      loginWithGithub: () => {},
      loginWithTwitter: () => {},
      register,
      logout,
    }),
    [state.isInitialized, state.isAuthenticated, state.user, login, googleLogin, register, logout]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
